import apiClient from './client'

export interface LoginCredentials {
  username: string // email
  password: string
}

export interface SignupData {
  email: string
  password: string
  full_name?: string
}

export interface User {
  id: string
  email: string
  full_name: string | null
  is_active: boolean
  is_superuser: boolean
  is_premium: boolean
  theme: string
  plan_type: string
  remaining_tokens: number
  tokens_refresh_time: number | null
}

export interface LoginResponse {
  access_token: string
  token_type: string
  username: string
}

// Login with email and password
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  // OAuth2 password flow requires form data
  const formData = new URLSearchParams()
  formData.append('username', credentials.username)
  formData.append('password', credentials.password)

  const response = await apiClient.post<LoginResponse>('/login/access-token', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
  return response.data
}

// Register new user
export async function signup(data: SignupData): Promise<User> {
  const response = await apiClient.post<User>('/users/signup', data)
  return response.data
}

// Get current user info
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.post<User>('/login/test-token')
  return response.data
}

// Logout - clear the auth cookie
export async function logout(): Promise<void> {
  await apiClient.post('/logout')
}

// Check if user is authenticated by testing token
export async function checkAuth(): Promise<User | null> {
  try {
    return await getCurrentUser()
  } catch {
    return null
  }
}

// Activate account via email verification token
export async function activateAccount(token: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`/activate_account/?token=${encodeURIComponent(token)}`)
  return response.data
}

// Store the browser extension UUID token on the current user
export async function updateExtensionToken(extUuid: string): Promise<User> {
  const response = await apiClient.patch<User>('/users/me/extension-token', { ext_uuid: extUuid })
  return response.data
}

